/**
 * Date formatting utilities
 * Ensures consistent date formatting across the application
 */

/**
 * Pads a number with leading zero if it's single digit
 * @param {number} num - The number to pad
 * @returns {string} - Padded string
 */
const pad = (num) => num.toString().padStart(2, '0');

/**
 * Formats a date to DD.MM.YYYY format with leading zeros
 * @param {Date|string} date - Date object or date string
 * @returns {string} - Formatted date string (DD.MM.YYYY)
 */
export const formatDateDDMMYYYY = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to formatDateDDMMYYYY:', date);
    return '';
  }
  
  const day = pad(dateObj.getDate());
  const month = pad(dateObj.getMonth() + 1); // Months are 0-indexed
  const year = dateObj.getFullYear();
  
  return `${day}.${month}.${year}`;
};

/**
 * Formats a date to YYYY-MM-DD format for input fields
 * @param {Date|string} date - Date object or date string
 * @returns {string} - Formatted date string (YYYY-MM-DD)
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to formatDateForInput:', date);
    return '';
  }
  
  const day = pad(dateObj.getDate());
  const month = pad(dateObj.getMonth() + 1);
  const year = dateObj.getFullYear();
  
  return `${year}-${month}-${day}`;
};

/**
 * Formats a datetime to input datetime-local format (YYYY-MM-DDTHH:mm)
 * @param {Date|string} date - Date object or date string
 * @returns {string} - Formatted datetime string
 */
export const formatDateTimeForInput = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to formatDateTimeForInput:', date);
    return '';
  }
  
  const day = pad(dateObj.getDate());
  const month = pad(dateObj.getMonth() + 1);
  const year = dateObj.getFullYear();
  const hours = pad(dateObj.getHours());
  const minutes = pad(dateObj.getMinutes());
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Formats a date with time to DD.MM.YYYY HH:mm:ss format
 * @param {Date|string} date - Date object or date string
 * @returns {string} - Formatted datetime string
 */
export const formatDateTimeDDMMYYYY = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to formatDateTimeDDMMYYYY:', date);
    return '';
  }
  
  const day = pad(dateObj.getDate());
  const month = pad(dateObj.getMonth() + 1);
  const year = dateObj.getFullYear();
  const hours = pad(dateObj.getHours());
  const minutes = pad(dateObj.getMinutes());
  const seconds = pad(dateObj.getSeconds());
  
  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
};

export default {
  formatDateDDMMYYYY,
  formatDateForInput,
  formatDateTimeForInput,
  formatDateTimeDDMMYYYY,
};



